/**
 * Patient details block — name, species, breed, sex, and optional extra fields.
 * Exports both a react-pdf version (for PDF generation) and an HTML version (for screen preview).
 *
 * Base fields (always shown): name, species, breed, sex (with neutered status).
 * Optional fields (controlled via boolean props, all default to false):
 *   showDob, showAge, showWeight, showMicrochip.
 */
import { StyleSheet, Text, View } from '@react-pdf/renderer'

import {
  formatAge,
  formatCalendarDate,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'

export interface PatientDetailsProps extends Pick<DocumentContext, 'patient' | 'weight'> {
  /** Show date of birth. Defaults to false. */
  showDob?: boolean
  /** Show age (derived from date of birth). Defaults to false. */
  showAge?: boolean
  /** Show weight. Defaults to false. */
  showWeight?: boolean
  /** Show microchip number. Defaults to false. */
  showMicrochip?: boolean
}

export function PatientDetailsPreview({
  patient,
  weight,
  showDob = false,
  showAge = false,
  showWeight = false,
  showMicrochip = false,
}: PatientDetailsProps) {
  // The preview is WYSIWYG with the PDF, so it follows the *document* rather than the
  // viewer: same resolution as the PDF half, off the owning member's region. Otherwise a
  // UK vet previewing a US member's certificate sees a different date order on screen
  // than in the file they download. DOB is a calendar day, so only the locale applies.
  const fmt = resolveDateFormat(null, patient.owner.region)
  return (
    <div className="leading-snug">
      <p>
        <span className="font-semibold">Patient: </span>
        {patient.name}
      </p>
      <p>
        <span className="font-semibold">Species: </span>
        {patient.species}
      </p>
      {patient.breed && (
        <p>
          <span className="font-semibold">Breed: </span>
          {patient.breed}
        </p>
      )}
      <p>
        <span className="font-semibold">Sex: </span>
        {patient.sex}
        {patient.isNeutered != null && (patient.isNeutered ? ' (Neutered)' : ' (Intact)')}
      </p>
      {showDob && patient.dateOfBirth && (
        <p>
          <span className="font-semibold">DOB: </span>
          {formatCalendarDate(patient.dateOfBirth, fmt)}
        </p>
      )}
      {showAge && patient.dateOfBirth && (
        <p>
          <span className="font-semibold">Age: </span>
          {formatAge(patient.dateOfBirth)}
        </p>
      )}
      {showWeight && weight && (
        <p>
          <span className="font-semibold">Weight: </span>
          {weight}
        </p>
      )}
      {showMicrochip && patient.microchipNumber && (
        <p>
          <span className="font-semibold">Microchip: </span>
          {patient.microchipNumber}
        </p>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  label: { fontFamily: 'Helvetica-Bold', fontSize: 10 },
  text: { fontSize: 10, marginBottom: 1 },
})

export function PatientDetails({
  patient,
  weight,
  showDob = false,
  showAge = false,
  showWeight = false,
  showMicrochip = false,
}: PatientDetailsProps) {
  // Generated PDF output with no viewer to follow — resolve from the owning member's region.
  const fmt = resolveDateFormat(null, patient.owner.region)
  return (
    <View wrap={false}>
      <Text style={styles.text}>
        <Text style={styles.label}>Patient: </Text>
        {patient.name}
      </Text>
      <Text style={styles.text}>
        <Text style={styles.label}>Species: </Text>
        {patient.species}
      </Text>
      {patient.breed && (
        <Text style={styles.text}>
          <Text style={styles.label}>Breed: </Text>
          {patient.breed}
        </Text>
      )}
      <Text style={styles.text}>
        <Text style={styles.label}>Sex: </Text>
        {patient.sex}
        {patient.isNeutered != null && (patient.isNeutered ? ' (Neutered)' : ' (Intact)')}
      </Text>
      {showDob && patient.dateOfBirth && (
        <Text style={styles.text}>
          <Text style={styles.label}>DOB: </Text>
          {formatCalendarDate(patient.dateOfBirth, fmt)}
        </Text>
      )}
      {showAge && patient.dateOfBirth && (
        <Text style={styles.text}>
          <Text style={styles.label}>Age: </Text>
          {formatAge(patient.dateOfBirth)}
        </Text>
      )}
      {showWeight && weight && (
        <Text style={styles.text}>
          <Text style={styles.label}>Weight: </Text>
          {weight}
        </Text>
      )}
      {showMicrochip && patient.microchipNumber && (
        <Text style={styles.text}>
          <Text style={styles.label}>Microchip: </Text>
          {patient.microchipNumber}
        </Text>
      )}
    </View>
  )
}
